/**
 * SessionTracker - Lightweight session duration tracking service
 *
 * Tracks session duration using browser lifecycle events and user activity.
 * Provides accurate session timing for PostHog analytics experiments.
 */

class SessionTracker {
  constructor() {
    this.sessionStart = Date.now();
    this.lastActivity = Date.now();
    this.isActive = true;
    this.pausedDuration = 0;
    this.pauseStart = null;

    this.initializeEventListeners();
  }

  initializeEventListeners() {
    // Browser lifecycle events
    document.addEventListener(
      "visibilitychange",
      this.handleVisibilityChange.bind(this)
    );
    window.addEventListener("beforeunload", this.handleSessionEnd.bind(this));
    window.addEventListener("focus", this.handleSessionResume.bind(this));
    window.addEventListener("blur", this.handleSessionPause.bind(this));

    // User activity tracking
    const activityEvents = ["mousedown", "keydown", "touchstart"];
    activityEvents.forEach((event) => {
      document.addEventListener(event, this.updateActivity.bind(this), {
        passive: true,
      });
    });
  }

  handleVisibilityChange() {
    if (document.hidden) {
      this.handleSessionPause();
    } else {
      this.handleSessionResume();
    }
  }

  handleSessionPause() {
    if (this.isActive) {
      this.isActive = false;
      this.pauseStart = Date.now();
    }
  }

  handleSessionResume() {
    if (!this.isActive && this.pauseStart) {
      this.pausedDuration += Date.now() - this.pauseStart;
      this.pauseStart = null;
    }
    this.isActive = true;
    this.updateActivity();
  }

  handleSessionEnd() {
    // Final session duration calculation before page unload
    const duration = this.getSessionDuration();
    console.log(`Session ended. Duration: ${duration} seconds`);
  }

  updateActivity() {
    this.lastActivity = Date.now();
  }

  /**
   * Get current session duration in seconds
   * @returns {number} Session duration in seconds
   */
  getSessionDuration() {
    const now = Date.now();
    let totalDuration = now - this.sessionStart;

    // Subtract paused time
    totalDuration -= this.pausedDuration;

    // If currently paused, subtract current pause duration
    if (!this.isActive && this.pauseStart) {
      totalDuration -= now - this.pauseStart;
    }

    // Return duration in seconds (rounded to 1 decimal place)
    return Math.round((totalDuration / 1000) * 10) / 10;
  }

  /**
   * Get session metadata for analytics
   * @returns {object} Session metadata
   */
  getSessionMetadata() {
    return {
      session_duration: this.getSessionDuration(),
      session_start: this.sessionStart,
      last_activity: this.lastActivity,
      is_active: this.isActive,
      total_paused_duration: this.pausedDuration / 1000,
    };
  }

  /**
   * Reset session tracking (useful for SPA navigation or user login/logout)
   */
  resetSession() {
    this.sessionStart = Date.now();
    this.lastActivity = Date.now();
    this.isActive = true;
    this.pausedDuration = 0;
    this.pauseStart = null;
  }
}

// Create singleton instance
const sessionTracker = new SessionTracker();

export default sessionTracker;
